/**
 * Composable for PDF Waybill Processing
 * Manages state and logic for processing waybill PDFs
 */

import { reactive, computed, ref } from "vue";
import {
  processWaybillPdf,
  blobToFile,
  isPdfFile,
  validatePdfSize
} from "@/utils/waybillProcessor";

/**
 * composable for waybill PDF processing
 * @returns {Object} Processing state and methods
 */
export function useWaybillProcessor() {
  const processingState = reactive({
    isProcessing: false,
    isEnabled: false,
    progress: 0,
    error: null,
    message: ""
  });

  const processingErrors = ref([]);

  // Computed properties
  const isProcessingActive = computed(() => processingState.isProcessing);
  const hasErrors = computed(() => processingErrors.value.length > 0);
  const errorMessages = computed(() => processingErrors.value);

  /**
   * Clear all error messages
   */
  function clearErrors() {
    processingErrors.value = [];
    processingState.error = null;
  }

  /**
   * Toggle PDF processing on/off
   */
  function toggleProcessing() {
    processingState.isEnabled = !processingState.isEnabled;
    clearErrors();
  }

  /**
   * Enable PDF processing
   */
  function enableProcessing() {
    processingState.isEnabled = true;
    clearErrors();
  }

  /**
   * Disable PDF processing
   */
  function disableProcessing() {
    processingState.isEnabled = false;
    clearErrors();
  }

  /**
   * Add error message
   */
  function addError(message) {
    processingErrors.value.push(message);
    processingState.error = message;
  }

  /**
   * Process PDF attachment
   * @param {File} pdfFile - PDF file to process
   * @returns {Promise<File|null>} Processed PDF file or null on error
   */
  async function processPdf(pdfFile) {
    // Reset state
    clearErrors();
    processingState.isProcessing = true;
    processingState.progress = 0;
    processingState.message = "Validating file...";

    try {
      // Validate file
      if (!isPdfFile(pdfFile)) {
        throw new Error(
          "Selected file is not a valid PDF. Please select a PDF file."
        );
      }

      if (!validatePdfSize(pdfFile)) {
        throw new Error("PDF file is too large. Maximum size is 50MB.");
      }

      processingState.message = "Processing waybill PDF...";

      // Process the PDF
      const processedBlob = await processWaybillPdf(pdfFile, {
        onProgress: progressData => {
          processingState.progress = progressData.percentage;
          processingState.message = `Processing page ${progressData.current} of ${progressData.total}...`;

          if (progressData.error) {
            addError(`Warning: ${progressData.error}`);
          }
        }
      });

      // Convert blob back to file
      const processedFile = blobToFile(processedBlob, pdfFile.name);

      processingState.message = "Processing complete";
      processingState.progress = 100;

      return processedFile;
    } catch (error) {
      const errorMessage =
        error.message || "Unknown error during PDF processing";
      addError(errorMessage);
      processingState.message = `Error: ${errorMessage}`;
      console.error("PDF Processing Error:", error);
      return null;
    } finally {
      processingState.isProcessing = false;
    }
  }

  /**
   * Reset processing state
   */
  function reset() {
    processingState.isProcessing = false;
    processingState.progress = 0;
    processingState.message = "";
    clearErrors();
  }

  return {
    // State
    processingState,
    processingErrors,

    // Computed
    isProcessingActive,
    hasErrors,
    errorMessages,

    // Methods
    toggleProcessing,
    enableProcessing,
    disableProcessing,
    clearErrors,
    addError,
    processPdf,
    reset
  };
}
